import React from 'react';
import { twMerge } from 'tailwind-merge';

import { JUSTIFIED_GALLERY_DEFAULT_HEIGHTS } from './constants';
import { JustifiedGalleryProps, JustifiedGalleryItem } from './types';
import { useJustifiedGalleryLayout } from './useJustifiedGalleryLayout';
import {
  calculateActualRowGap,
  getItemRenderData,
  getItemStyleProps,
  getGapStyle,
} from './utils';

import './justifiedGallery.css';

export const JustifiedGallery = <T = JustifiedGalleryItem,>({
  items,
  responsiveHeights = JUSTIFIED_GALLERY_DEFAULT_HEIGHTS,
  rowGapPx = 8,
  columnGapPx = 8,
  className,
  children,
  lastRowStretchThreshold = 0.9,
  shouldRenderPortalContainer = false,
  ...rest
}: JustifiedGalleryProps<T>) => {
  const actualRowGapPx = calculateActualRowGap(
    rowGapPx,
    shouldRenderPortalContainer
  );

  const { rows, isHydrated, lastRowIndex, shouldLastRowStretch, containerRef } =
    useJustifiedGalleryLayout({
      items,
      responsiveHeights,
      lastRowStretchThreshold,
      rowGapPx,
      columnGapPx,
    });
  return (
    <div
      ref={containerRef}
      data-testid="justified-gallery"
      className={twMerge('flex flex-wrap justified-gallery', className)}
      style={
        {
          gap: getGapStyle(actualRowGapPx, columnGapPx),
          // defines responsive height variables for gallery items used in css files
          '--h-sm': `${responsiveHeights?.sm}px`,
          '--h-md': `${responsiveHeights?.md}px`,
          '--h-lg': `${responsiveHeights?.lg}px`,
          '--h-xl': `${responsiveHeights?.xl}px`,
        } as React.CSSProperties
      }
      {...rest}
    >
      {items.map((item, index) => {
        const renderData = getItemRenderData(
          item,
          index,
          rows,
          lastRowIndex,
          shouldLastRowStretch
        );

        const styleProps = getItemStyleProps(renderData);
        const { isLastItemInRow, rowIndex } = renderData;
        return (
          <React.Fragment key={item.id}>
            <div
              data-testid={`justified-gallery-item-${item.id}`}
              className="justified-gallery-item"
              style={styleProps}
            >
              {children(renderData)}
            </div>
            {shouldRenderPortalContainer && isHydrated && isLastItemInRow && (
              <div
                data-portal-container-id={`portal-container-${rowIndex}`}
                className="w-full"
                style={{ flexBasis: '100%' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
